/**
 * Filter container — the single player-filter surface (SE-5548).
 *
 * Reads `showFilterVar` to open/close the MobileFilterSheet. Owns filter
 * state via the shared `useFilterState` hook so every screen that uses
 * HeaderWatchList gets filtering for free — no per-screen wiring needed.
 * Presents as a bottom sheet on small screens and as a centered modal on
 * desktop/iPad via the `presentation` prop (the legacy ModalFilter side
 * panel was folded into this in SE-5548).
 *
 * When "Apply Filters" is pressed, converts `useFilterState` values into
 * `filterValuesVar` (the legacy reactive variable read by `useFilter` /
 * `FilterResults`) so existing query plumbing picks up the selections. (SE-4292)
 * @module components/MobileFilter/MobileFilterContainer
 */

import { useReactiveVar } from "@apollo/client";
import { useCallback, useEffect, useMemo } from "react";

import { TEST_ID_WATCHLIST } from "e2e/testId";

import MobileFilterSheet, {
  type FilterSheetPresentation,
} from "@/components/organisms/shared/MobileFilterSheet";
import {
  DEFAULT_AGE_RANGE,
  DEFAULT_CONTRACT_MONTHS_RANGE,
  DEFAULT_SCORE_RANGE,
  DEFAULT_VALUATION_RANGE,
  isRangeActive,
} from "@/constants/filterDefaults";
import useFilterState, {
  DEFAULT_FILTER_SNAPSHOT,
  type FilterSnapshot,
} from "@/hooks/useFilterState";
import toggleId from "@/utils/toggleId";
import { useAvailableCustomMetrics } from "@/hooks/useAvailableCustomMetrics";
import {
  filterValuesVar,
  type IFilterValues,
  type IFilterValueRange,
  showFilterVar,
} from "@/stores/filterValues";
import { getCustomMetricFilterFieldName } from "@/types/customMetrics";

import type { FilterOption } from "@/types/filter";

/**
 * Converts a legacy {min, max} range back to a [min, max] tuple,
 * falling back to the provided default when the range is absent.
 * @param range - Legacy range object or undefined
 * @param fallback - Default tuple to use when range is absent
 */
const fromRange = (
  range: IFilterValueRange | undefined,
  fallback: [number, number]
): [number, number] => (range ? [range.min, range.max] : fallback);

/**
 * Converts a legacy `IFilterValues` object back into a `FilterSnapshot`
 * so `useFilterState.restoreSnapshot` can hydrate the hook state.
 * @param fv - Legacy filter values from filterValuesVar
 */
const toSnapshot = (fv: IFilterValues): FilterSnapshot => ({
  selectedPositionIds: fv.positions?.map(p => p.id) ?? [],
  selectedClubIds: fv.clubs?.map(c => c.id) ?? [],
  selectedScoutIds: [],
  selectedNationalityIds: fv.nationalities?.map(n => n.id) ?? [],
  selectedLeagueIds: fv.leagues?.map(l => l.id) ?? [],
  selectedCompetitionIds: fv.competitions?.map(c => c.id) ?? [],
  selectedRoleArchetypes: fv.roles?.map(r => r.id) ?? [],
  ageRange: fromRange(fv.age, DEFAULT_FILTER_SNAPSHOT.ageRange),
  contractMonthsRange: fromRange(
    fv.monthsRemaining,
    DEFAULT_FILTER_SNAPSHOT.contractMonthsRange
  ),
  valuationRange: fromRange(fv.salary, DEFAULT_FILTER_SNAPSHOT.valuationRange),
  gprRange: fromRange(fv.gpr, DEFAULT_FILTER_SNAPSHOT.gprRange),
  fitScoreRange: fromRange(
    fv.playerStyleFit,
    DEFAULT_FILTER_SNAPSHOT.fitScoreRange
  ),
  teamStyleFitRange: fromRange(
    fv.teamStyleFit,
    DEFAULT_FILTER_SNAPSHOT.teamStyleFitRange
  ),
  carryingRange: fromRange(fv.carrying, DEFAULT_FILTER_SNAPSHOT.carryingRange),
  dribblingRange: fromRange(
    fv.dribbling,
    DEFAULT_FILTER_SNAPSHOT.dribblingRange
  ),
  passingRange: fromRange(fv.passing, DEFAULT_FILTER_SNAPSHOT.passingRange),
  shootingRange: fromRange(fv.shooting, DEFAULT_FILTER_SNAPSHOT.shootingRange),
  gkPassingRange: fromRange(
    fv.gkPassing,
    DEFAULT_FILTER_SNAPSHOT.gkPassingRange
  ),
  gkShotStoppingRange: fromRange(
    fv.gkShotStopping,
    DEFAULT_FILTER_SNAPSHOT.gkShotStoppingRange
  ),
  gkPositioningRange: fromRange(
    fv.gkPositioning,
    DEFAULT_FILTER_SNAPSHOT.gkPositioningRange
  ),
  customStatRanges: {},
});

/**
 * Resolves a list of selected IDs to their full option objects.
 * @param ids - Selected IDs
 * @param options - Available options with id + label
 */
const resolveOptions = (
  ids: readonly string[],
  options: readonly FilterOption[]
): { id: string; name: string }[] =>
  ids
    .map(id => {
      const opt = options.find(o => o.id === id);
      return opt ? { id: opt.id, name: opt.label } : null;
    })
    .filter((o): o is { id: string; name: string } => o !== null);

/**
 * Converts a [min, max] tuple to the legacy {min, max} range shape,
 * or undefined if the tuple is missing.
 * @param range - [min, max] tuple
 */
const toRange = (range: [number, number]) => ({
  min: range[0],
  max: range[1],
});

/**
 * Props for MobileFilterContainer
 */
interface MobileFilterProps {
  /**
   * Presentation mode forwarded to MobileFilterSheet: "actionsheet" (bottom
   * sheet, small screens) or "modal" (centered modal, desktop/iPad —
   * SE-5548, replaces the legacy ModalFilter side panel). Defaults to
   * "actionsheet".
   */
  readonly presentation?: FilterSheetPresentation;
}

/**
 * Container that bridges `showFilterVar` (reactive open/close trigger)
 * with the shared `useFilterState` hook and renders `MobileFilterSheet`.
 * @param props - Component props
 * @param props.presentation - Presentation mode forwarded to MobileFilterSheet
 */
const MobileFilterContainer = ({ presentation }: MobileFilterProps) => {
  const isOpen = useReactiveVar(showFilterVar);

  // The custom-metric section and the active-filter badge render only in the
  // open sheet, which mounts closed on the player profile (SE-8832).
  const { availableCustomMetrics } = useAvailableCustomMetrics({
    enabled: isOpen,
  });
  const availableMetricIds = useMemo(
    () => new Set(availableCustomMetrics.map(m => m.id)),
    [availableCustomMetrics]
  );
  const {
    roleOptions,
    selectedRoleArchetypes,
    setSelectedRoleArchetypes,
    clubOptions,
    selectedClubIds,
    setSelectedClubIds,
    setClubSearchText,
    clubSearching,
    positionOptions,
    selectedPositionIds,
    setSelectedPositionIds,
    leagueOptions,
    selectedLeagueIds,
    setSelectedLeagueIds,
    competitionOptions,
    selectedCompetitionIds,
    setSelectedCompetitionIds,
    nationalityOptions,
    selectedNationalityIds,
    setSelectedNationalityIds,
    ageRange,
    setAgeRange,
    valuationRange,
    setValuationRange,
    contractMonthsRange,
    setContractMonthsRange,
    gprRange,
    setGprRange,
    fitScoreRange,
    setFitScoreRange,
    teamStyleFitRange,
    setTeamStyleFitRange,
    carryingRange,
    setCarryingRange,
    dribblingRange,
    setDribblingRange,
    passingRange,
    setPassingRange,
    shootingRange,
    setShootingRange,
    gkPassingRange,
    setGkPassingRange,
    gkShotStoppingRange,
    setGkShotStoppingRange,
    gkPositioningRange,
    setGkPositioningRange,
    customStatRanges,
    setCustomStatRange,
    clearAllFilters,
    restoreSnapshot,
    optionsLoading,
    activeFilterCount,
  } = useFilterState(availableMetricIds);

  /* --- Adapter callbacks for MobileFilterSheet single-select / toggle API --- */

  const handleClose = useCallback(() => {
    showFilterVar(false);
  }, []);

  /**
   * Convert useFilterState values to IFilterValues and write to filterValuesVar.
   * Only includes filters with actual selections — empty arrays become null and
   * ranges at their default values are omitted so the API doesn't over-constrain.
   */
  const handleApply = useCallback(() => {
    const orNull = (arr: { id: string; name: string }[]) =>
      arr.length > 0 ? arr : null;

    const rangeOrUndefined = (
      range: [number, number],
      defaultRange: [number, number]
    ) => (isRangeActive(range, defaultRange) ? toRange(range) : undefined);

    const customMetricFilters = Object.entries(customStatRanges).reduce<
      Partial<IFilterValues>
    >((acc, [key, range]) => {
      const filterKey = getCustomMetricFilterFieldName(key);
      return { ...acc, [filterKey]: toRange(range as [number, number]) };
    }, {});

    const filterValues: IFilterValues = {
      roles: orNull(resolveOptions(selectedRoleArchetypes, roleOptions)),
      positions: orNull(
        resolveOptions(selectedPositionIds, positionOptions)
      ) as IFilterValues["positions"],
      leagues: orNull(
        resolveOptions(selectedLeagueIds, leagueOptions)
      ) as IFilterValues["leagues"],
      competitions: orNull(
        resolveOptions(selectedCompetitionIds, competitionOptions)
      ),
      nationalities: orNull(
        resolveOptions(selectedNationalityIds, nationalityOptions)
      ) as IFilterValues["nationalities"],
      clubs: orNull(
        resolveOptions(selectedClubIds, clubOptions)
      ) as IFilterValues["clubs"],
      age: rangeOrUndefined(ageRange, DEFAULT_AGE_RANGE),
      salary: rangeOrUndefined(valuationRange, DEFAULT_VALUATION_RANGE),
      monthsRemaining: rangeOrUndefined(
        contractMonthsRange,
        DEFAULT_CONTRACT_MONTHS_RANGE
      ),
      gpr: rangeOrUndefined(gprRange, DEFAULT_SCORE_RANGE),
      playerStyleFit: rangeOrUndefined(fitScoreRange, DEFAULT_SCORE_RANGE),
      teamStyleFit: rangeOrUndefined(teamStyleFitRange, DEFAULT_SCORE_RANGE),
      carrying: rangeOrUndefined(carryingRange, DEFAULT_SCORE_RANGE),
      dribbling: rangeOrUndefined(dribblingRange, DEFAULT_SCORE_RANGE),
      passing: rangeOrUndefined(passingRange, DEFAULT_SCORE_RANGE),
      shooting: rangeOrUndefined(shootingRange, DEFAULT_SCORE_RANGE),
      gkPassing: rangeOrUndefined(gkPassingRange, DEFAULT_SCORE_RANGE),
      gkPositioning: rangeOrUndefined(gkPositioningRange, DEFAULT_SCORE_RANGE),
      gkShotStopping: rangeOrUndefined(
        gkShotStoppingRange,
        DEFAULT_SCORE_RANGE
      ),
      ...customMetricFilters,
    };

    // When no actual filters are active (all arrays null, all ranges at
    // defaults), reset to null so the search tab returns to its initial
    // state instead of showing an empty FilterResults view.
    const hasActiveFilter =
      filterValues.roles !== null ||
      filterValues.positions !== null ||
      filterValues.leagues !== null ||
      filterValues.competitions !== null ||
      filterValues.nationalities !== null ||
      filterValues.clubs !== null ||
      filterValues.age !== undefined ||
      filterValues.salary !== undefined ||
      filterValues.monthsRemaining !== undefined ||
      filterValues.gpr !== undefined ||
      filterValues.playerStyleFit !== undefined ||
      filterValues.teamStyleFit !== undefined ||
      filterValues.carrying !== undefined ||
      filterValues.dribbling !== undefined ||
      filterValues.passing !== undefined ||
      filterValues.shooting !== undefined ||
      filterValues.gkPassing !== undefined ||
      filterValues.gkPositioning !== undefined ||
      filterValues.gkShotStopping !== undefined ||
      Object.keys(customMetricFilters).length > 0;

    filterValuesVar(hasActiveFilter ? filterValues : null);
    showFilterVar(false);
  }, [
    selectedRoleArchetypes,
    roleOptions,
    selectedPositionIds,
    positionOptions,
    selectedLeagueIds,
    leagueOptions,
    selectedCompetitionIds,
    competitionOptions,
    selectedNationalityIds,
    nationalityOptions,
    ageRange,
    valuationRange,
    contractMonthsRange,
    gprRange,
    fitScoreRange,
    teamStyleFitRange,
    carryingRange,
    dribblingRange,
    passingRange,
    shootingRange,
    gkPassingRange,
    gkPositioningRange,
    gkShotStoppingRange,
    customStatRanges,
    selectedClubIds,
    clubOptions,
  ]);

  const handleClearAll = useCallback(() => {
    clearAllFilters();
    filterValuesVar(null);
  }, [clearAllFilters]);

  const handleToggleRole = useCallback(
    (id: string) =>
      setSelectedRoleArchetypes(toggleId(selectedRoleArchetypes, id)),
    [selectedRoleArchetypes, setSelectedRoleArchetypes]
  );

  const handleClearRoles = useCallback(
    () => setSelectedRoleArchetypes([]),
    [setSelectedRoleArchetypes]
  );

  const handleTogglePosition = useCallback(
    (id: string) => setSelectedPositionIds(toggleId(selectedPositionIds, id)),
    [selectedPositionIds, setSelectedPositionIds]
  );

  const handleClearPositions = useCallback(
    () => setSelectedPositionIds([]),
    [setSelectedPositionIds]
  );

  const handleToggleLeague = useCallback(
    (id: string) => setSelectedLeagueIds(toggleId(selectedLeagueIds, id)),
    [selectedLeagueIds, setSelectedLeagueIds]
  );

  const handleClearLeagues = useCallback(
    () => setSelectedLeagueIds([]),
    [setSelectedLeagueIds]
  );

  const handleToggleCompetition = useCallback(
    (id: string) =>
      setSelectedCompetitionIds(toggleId(selectedCompetitionIds, id)),
    [selectedCompetitionIds, setSelectedCompetitionIds]
  );

  const handleClearCompetitions = useCallback(
    () => setSelectedCompetitionIds([]),
    [setSelectedCompetitionIds]
  );

  const handleToggleClub = useCallback(
    (id: string) => setSelectedClubIds(toggleId(selectedClubIds, id)),
    [selectedClubIds, setSelectedClubIds]
  );

  const handleClearClubs = useCallback(
    () => setSelectedClubIds([]),
    [setSelectedClubIds]
  );

  const handleToggleNationality = useCallback(
    (id: string) =>
      setSelectedNationalityIds(toggleId(selectedNationalityIds, id)),
    [selectedNationalityIds, setSelectedNationalityIds]
  );

  const handleClearNationalities = useCallback(
    () => setSelectedNationalityIds([]),
    [setSelectedNationalityIds]
  );

  /* Hydrate useFilterState from filterValuesVar on mount so previously
     applied filters are reflected when the sheet re-opens after the
     component remounts (e.g. when FilterResults replaces MainView). */
  useEffect(() => {
    const fv = filterValuesVar();
    if (fv) {
      restoreSnapshot(toSnapshot(fv));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only on mount
  }, []);

  // Temporary debug log to diagnose empty filter options (SE-4292)
  useEffect(() => {
    console.log("[MobileFilter] Filter options state:", {
      roleCount: roleOptions.length,
      positionCount: positionOptions.length,
      leagueCount: leagueOptions.length,
      nationalityCount: nationalityOptions.length,
      clubCount: clubOptions.length,
      optionsLoading,
      isOpen,
    });
  }, [
    roleOptions.length,
    positionOptions.length,
    leagueOptions.length,
    nationalityOptions.length,
    clubOptions.length,
    optionsLoading,
    isOpen,
  ]);

  return (
    <MobileFilterSheet
      presentation={presentation}
      isOpen={isOpen}
      showResultsTestID={TEST_ID_WATCHLIST.FILTER_SEARCH_BUTTON}
      onClose={handleClose}
      onApply={handleApply}
      onClearAll={handleClearAll}
      activeFilterCount={activeFilterCount}
      roleOptions={roleOptions}
      selectedRoleArchetypes={selectedRoleArchetypes}
      onToggleRole={handleToggleRole}
      onClearRoles={handleClearRoles}
      positionOptions={positionOptions}
      selectedPositionIds={selectedPositionIds}
      onTogglePosition={handleTogglePosition}
      onClearPositions={handleClearPositions}
      leagueOptions={leagueOptions}
      selectedLeagueIds={selectedLeagueIds}
      onToggleLeague={handleToggleLeague}
      onClearLeagues={handleClearLeagues}
      onChangeLeaguePreset={setSelectedLeagueIds}
      competitionOptions={competitionOptions}
      selectedCompetitionIds={selectedCompetitionIds}
      onToggleCompetition={handleToggleCompetition}
      onClearCompetitions={handleClearCompetitions}
      clubOptions={clubOptions}
      selectedClubIds={selectedClubIds}
      onToggleClub={handleToggleClub}
      onClearClubs={handleClearClubs}
      clubSearchText={undefined}
      onClubSearchChange={setClubSearchText}
      clubSearching={clubSearching}
      nationalityOptions={nationalityOptions}
      selectedNationalityIds={selectedNationalityIds}
      onToggleNationality={handleToggleNationality}
      onClearNationalities={handleClearNationalities}
      ageRange={ageRange}
      onSetAgeRange={setAgeRange}
      valuationRange={valuationRange}
      onSetValuationRange={setValuationRange}
      contractMonthsRange={contractMonthsRange}
      onSetContractMonthsRange={setContractMonthsRange}
      gprRange={gprRange}
      onSetGprRange={setGprRange}
      fitScoreRange={fitScoreRange}
      onSetFitScoreRange={setFitScoreRange}
      teamStyleFitRange={teamStyleFitRange}
      onSetTeamStyleFitRange={setTeamStyleFitRange}
      carryingRange={carryingRange}
      onSetCarryingRange={setCarryingRange}
      dribblingRange={dribblingRange}
      onSetDribblingRange={setDribblingRange}
      passingRange={passingRange}
      onSetPassingRange={setPassingRange}
      shootingRange={shootingRange}
      onSetShootingRange={setShootingRange}
      gkPassingRange={gkPassingRange}
      onSetGkPassingRange={setGkPassingRange}
      gkShotStoppingRange={gkShotStoppingRange}
      onSetGkShotStoppingRange={setGkShotStoppingRange}
      gkPositioningRange={gkPositioningRange}
      onSetGkPositioningRange={setGkPositioningRange}
      customMetrics={availableCustomMetrics}
      customStatRanges={customStatRanges}
      onSetCustomStatRange={setCustomStatRange}
    />
  );
};

export default MobileFilterContainer;
